import '../styles/panda.css';
import '@zillow/constellation-fonts/zillow-fonts.css';

import type { AppProps } from 'next/app';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { Head } from '@zg-rentals/next/pages';
import { useRegisterNotification } from '@zg-rentals/register-notification';
import { useSiftScience } from '@zg-rentals/sift-science';
import { useRouter } from 'next/router';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { DefaultQueryProvider } from '@zg-rentals/react-query5/client';
import { zillowLoginRedirectHref } from '@zg-rentals/login-zillow/client';
import { ErrorPage } from '@zg-rentals/visual-cohesion';
import { Box, Spinner, ToastProvider } from '@zillow/constellation-10';
import { trackComscoreEvent } from '../analytics';
import { Auth, useAuth } from '../AuthProvider';

const authPendingFallback = (
  <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
    <Spinner size="lg" />
  </Box>
);

const UserAnalytics = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const router = useRouter();

  const { email, impersonatorUserToken } = user ?? {};

  useSiftScience({ email, isImpersonator: Boolean(impersonatorUserToken) });
  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);
  useRegisterNotification({ ref: 'RENTER_PROFILE_WEB' });

  return children;
};

const MyApp = ({ Component, pageProps }: AppProps) => {
  useBootstrap(() => import('../bootstrap/browser'));

  return (
    <>
      <Head applicationName="renter-profile-web" buildRef={process.env.NEXT_PUBLIC_BUILD_REF}>
        <title>Renter profile | Zillow</title>
      </Head>

      <RpErrorBoundary>
        <DefaultQueryProvider>
          <Auth.Provider>
            <Auth.Boundary
              redirectHref={zillowLoginRedirectHref({
                baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
                entryPoint: 'renter-profile-web',
              })}
              onUnauthorized={() => <ErrorPage errorMessage="Sign in to view your renter profile." />}
              loading={authPendingFallback}
              redirecting={authPendingFallback}
            >
              <UserAnalytics>
                <ToastProvider>
                  <Component {...pageProps} />
                </ToastProvider>
              </UserAnalytics>
            </Auth.Boundary>
          </Auth.Provider>
        </DefaultQueryProvider>
      </RpErrorBoundary>
    </>
  );
};

export default MyApp;
